/**
 * Sothiares Pre-Order Widget
 * ==========================
 * The pre-order copy + button-relabel logic lives inside blocks/buy-buttons.liquid
 * (server-rendered alongside the Add to Cart button). This script handles the
 * client-side variant-change reactivity:
 *
 *   1. Toggles the <pre-order-block> element's visibility on variant change
 *      based on the 4-condition predicate
 *      (metafield + inventory_management == 'shopify'
 *       + inventory_quantity <= 0 + inventory_policy == 'continue').
 *
 *   2. Overwrites the native Atelier Add to Cart button label when in
 *      pre-order state; restores the original on exit. The button's
 *      disabled state is NOT touched (Shopify's can_add_to_cart flag
 *      controls it; always true with continue-selling on).
 *
 *   3. Tags /cart/add.js requests for pre-order variants with a private
 *      _pre_order: true line-item property when the block setting
 *      tag_as_preorder is enabled.
 *
 * Atelier emits `variant:update` (NOT `variant:change` like Dawn).
 * Listening to `variant:selected` would fire too early (payload only
 * carries { id } before the variant data resolves).
 */

import { ThemeEvents } from '@theme/events';

class PreOrderBlock extends HTMLElement {
  connectedCallback() {
    const section = this.closest('.shopify-section, dialog');
    section?.addEventListener(ThemeEvents.variantUpdate, this.handleVariantUpdate);

    PreOrderBlock.installFetchInterceptor();

    // Apply initial ATC button override based on server-rendered state.
    // The element's `hidden` attribute is set by Liquid on first paint;
    // mirror that into the button label so there's no flash of incorrect
    // CTA text when landing on a pre-order variant via ?variant=.
    this.applyAtcButtonOverride(!this.hidden);
  }

  disconnectedCallback() {
    const section = this.closest('.shopify-section, dialog');
    section?.removeEventListener(ThemeEvents.variantUpdate, this.handleVariantUpdate);
  }

  handleVariantUpdate = (event) => {
    // Multi-product safety guard: ignore events for other products in the
    // same section (combined listings, quick-add dialogs).
    const eventProductId = event.detail?.data?.productId
      ?? event.detail?.data?.newProduct?.id;

    if (eventProductId && String(eventProductId) !== String(this.dataset.productId)) {
      return;
    }

    // Atelier-native pattern: variant:update carries event.detail.data.html
    // — the FULL freshly server-rendered section HTML for the new variant.
    // Server-side Liquid is the source of truth (it re-evaluates the
    // 4-condition predicate with full access to variant.inventory_quantity
    // and variant.inventory_policy, which are NOT on event.detail.resource).
    // Extract the fresh pre-order-block from that HTML and mirror its state.
    const newHtml = event.detail?.data?.html;
    if (!newHtml) return;

    const newPreOrder = newHtml.querySelector('pre-order-block');
    const debugMode = this.dataset.debugMode === 'true';

    if (!newPreOrder) {
      // Server-rendered output omitted the pre-order block for this
      // variant (predicate didn't match server-side). Hide and bail.
      // Exception: if debug mode is on the element stays visible.
      if (!debugMode) {
        this.hidden = true;
      }
      this.applyAtcButtonOverride(false);
      return;
    }

    // Mirror the server-rendered hidden state. The Liquid block sets
    // [hidden] when initial_is_preorder is false. If debug mode is on,
    // the server never sets [hidden] — both ours and the new one are
    // visible.
    if (debugMode) {
      this.hidden = false;
    } else {
      this.hidden = newPreOrder.hasAttribute('hidden');
    }

    // Sync the debug badge content if both exist.
    const newDebug = newPreOrder.querySelector('[data-pre-order-debug]');
    const existingDebug = this.querySelector('[data-pre-order-debug]');
    if (newDebug && existingDebug) {
      existingDebug.innerHTML = newDebug.innerHTML;
    }

    // Update the ATC button label based on the new state.
    this.applyAtcButtonOverride(!this.hidden);
  };

  /**
   * Overwrites the native Atelier Add to Cart button label when the
   * current variant is in pre-order state; restores the original on
   * exit. Caches the original on data-pre-order-original-label so
   * re-entry after morph() (which can replace the button DOM) restores
   * cleanly.
   *
   * Targeting follows Atelier's snippets/add-to-cart-button.liquid:
   * <button name="add"> contains <span class="add-to-cart-text__content">
   * with the visible label. We modify the inner span, not the button
   * itself, so the icon and "added" state animations stay intact.
   *
   * The button's disabled state is NOT touched — Shopify's
   * can_add_to_cart flag controls it. With inventory_policy: 'continue'
   * the button stays enabled at negative stock.
   */
  applyAtcButtonOverride = (isPreOrder) => {
    const section = this.closest('.shopify-section, dialog');
    if (!section) return;

    const buttons = section.querySelectorAll('button[name="add"]');
    // Respect explicit-blank from the merchant: if the merchant cleared
    // the CTA label setting, do NOT relabel the button. Empty string
    // means "leave the native label as-is" — never overlay nothing.
    const label = (this.dataset.ctaLabel || '').trim();

    buttons.forEach((btn) => {
      const textNode = btn.querySelector('.add-to-cart-text__content');
      if (!textNode) return;

      if (isPreOrder && label) {
        if (!textNode.dataset.preOrderOriginalLabel) {
          textNode.dataset.preOrderOriginalLabel = textNode.textContent.trim();
        }
        if (textNode.textContent.trim() !== label) {
          textNode.textContent = label;
        }
        btn.classList.add('is-pre-order');
      } else if (textNode.dataset.preOrderOriginalLabel) {
        textNode.textContent = textNode.dataset.preOrderOriginalLabel;
        delete textNode.dataset.preOrderOriginalLabel;
        btn.classList.remove('is-pre-order');
      }
    });
  };

  /**
   * Patch window.fetch once per page load. Intercepts POST /cart/add.js
   * (Shopify Cart Ajax API) and tags pre-order line items.
   *
   * TWO properties are injected, deliberately:
   *
   *   _pre_order: "true"        private (underscore prefix). Hidden from the
   *                             customer, visible in Admin. Kept for order
   *                             filtering and fulfilment.
   *
   *   "<label>": "<eta>"        VISIBLE, e.g. "Pre-order": "Ships in 4-6 weeks".
   *
   * The visible one is the point. Everything on the product page announcing
   * pre-order — heading, delivery line, relabelled button — stops at the
   * add-to-cart boundary. With only the private property, the cart drawer,
   * the checkout, the confirmation email and the packing slip all show the
   * item as an ordinary purchase, which is exactly where a customer stops
   * understanding they are waiting weeks for it.
   *
   * A visible line-item property is rendered by Shopify in all four of those
   * places with no extra work, which also means it reaches SlideCart without
   * needing to inject into its DOM — that drawer is a closed third-party app
   * we cannot template.
   */
  static _fetchPatched = false;

  /**
   * Builds the property set for a pre-order line item.
   *
   * @param {HTMLElement} block - the <pre-order-block> carrying the config
   * @returns {Record<string, string>}
   */
  static preOrderProperties(block) {
    const props = { _pre_order: 'true' };

    const label = (block.dataset.cartLabel || '').trim();
    const value = (block.dataset.cartValue || '').trim();

    // Only add the visible property when the merchant has given it a label.
    // An empty label would produce properties[] and Shopify would reject it.
    if (label) props[label] = value || 'Yes';

    return props;
  }

  static installFetchInterceptor() {
    if (PreOrderBlock._fetchPatched) return;
    PreOrderBlock._fetchPatched = true;

    const originalFetch = window.fetch.bind(window);

    window.fetch = async function patchedFetch(input, init = {}) {
      try {
        const url = typeof input === 'string' ? input : input?.url ?? '';

        if (!/\/cart\/add(\.js)?/.test(url)) {
          return originalFetch(input, init);
        }

        const block = document.querySelector('pre-order-block[data-tag-as-preorder="true"]');
        if (!block) {
          return originalFetch(input, init);
        }

        const preOrderIds = (block.dataset.preOrderVariantIds || '')
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);

        if (preOrderIds.length === 0) {
          return originalFetch(input, init);
        }

        const newInit = { ...init };
        const properties = PreOrderBlock.preOrderProperties(block);

        if (newInit.body instanceof FormData) {
          const id = newInit.body.get('id');
          if (id && preOrderIds.includes(String(id))) {
            for (const [key, value] of Object.entries(properties)) {
              const formKey = `properties[${key}]`;
              if (!newInit.body.has(formKey)) {
                newInit.body.append(formKey, value);
              }
            }
          }
        } else if (typeof newInit.body === 'string') {
          try {
            const parsed = JSON.parse(newInit.body);
            const id = parsed?.id ?? parsed?.items?.[0]?.id;
            if (id && preOrderIds.includes(String(id))) {
              if (Array.isArray(parsed.items)) {
                parsed.items = parsed.items.map((item) => {
                  if (preOrderIds.includes(String(item.id))) {
                    item.properties = { ...(item.properties || {}), ...properties };
                  }
                  return item;
                });
              } else {
                parsed.properties = { ...(parsed.properties || {}), ...properties };
              }
              newInit.body = JSON.stringify(parsed);
              if (newInit.headers && !newInit.headers['Content-Type'] && !newInit.headers['content-type']) {
                newInit.headers = { ...newInit.headers, 'Content-Type': 'application/json' };
              }
            }
          } catch (_) {
            // Body wasn't JSON; pass through unchanged.
          }
        }

        return originalFetch(input, newInit);
      } catch (err) {
        console.warn('[pre-order-block] fetch interceptor error, passing through:', err);
        return originalFetch(input, init);
      }
    };
  }
}

if (!customElements.get('pre-order-block')) {
  customElements.define('pre-order-block', PreOrderBlock);
}
